import { useTranslation } from 'react-i18next';

import { apiPut } from '@/api/client';
import type { Consent } from '@/api/types';
import { SubmitError } from '@/app/components/SubmitError';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { useMe } from '@/features/me/MeContext';
import { useSubmit } from '@/lib/useSubmit';

const STATUS_TONE = {
  pending: 'warning',
  granted: 'mint',
  denied: 'outline',
} as const;

/**
 * The caller's own consent rows (§ Accountmenu › 1, § Statistiek-elementen):
 * pending answers sorted to the top. `PUT /me/consents` is the only consent
 * write path in the whole API and answers only the caller's own rows — no
 * admin answers for anyone. Withdrawal is an answer of "denied", never a
 * delete.
 */
export function Consents() {
  const { t } = useTranslation();
  const { me, refresh } = useMe();
  const consents = [...(me.consents ?? [])].sort((a, b) => {
    const rank = (c: Consent) => (c.status === 'pending' ? 0 : 1);
    return rank(a) - rank(b);
  });
  const answer = useSubmit(async (consentId: string, status: 'granted' | 'denied') => {
    await apiPut('/me/consents', { body: { consent_id: consentId, status } });
    refresh();
  });

  if (consents.length === 0) {
    return <EmptyState text={t('consents.empty')} />;
  }

  return (
    <>
      <DataTable
        rowKey={(consent) => consent.id ?? ''}
        columns={[
          {
            key: 'department',
            header: t('consents.table.department'),
            render: (consent) => consent.department_name ?? consent.scope_id,
          },
          {
            key: 'status',
            header: t('consents.table.status'),
            render: (consent) => (
              <Badge tone={STATUS_TONE[consent.status ?? 'pending']}>
                {t(`consents.status.${consent.status ?? 'pending'}`)}
              </Badge>
            ),
          },
          {
            key: 'basis',
            header: t('consents.table.basis'),
            render: (consent) => (
              <span className="flex flex-col gap-1">
                <Badge tone="outline">
                  {t(`consents.basis.${consent.effective_basis ?? 'personal'}`)}
                </Badge>
                {consent.effective_basis === 'collective' && (
                  <span className="text-accent text-[13px]">{t('consents.collectiveNote')}</span>
                )}
              </span>
            ),
          },
          {
            key: 'actions',
            header: '',
            align: 'end',
            render: (consent) => {
              const answerable = consent.effective_basis === 'personal';
              if (!answerable) return null;
              return (
                <div className="flex justify-end gap-2">
                  {consent.status !== 'granted' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={answer.pending}
                      onClick={() => void answer.submit(consent.id ?? '', 'granted')}
                    >
                      {t('consents.actions.grant')}
                    </Button>
                  )}
                  {consent.status !== 'denied' && (
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={answer.pending}
                      onClick={() => void answer.submit(consent.id ?? '', 'denied')}
                    >
                      {consent.status === 'granted'
                        ? t('consents.actions.withdraw')
                        : t('consents.actions.deny')}
                    </Button>
                  )}
                </div>
              );
            },
          },
        ]}
        rows={consents}
      />
      <SubmitError error={answer.error} />
    </>
  );
}
